import { useState, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom'
import { stationsAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import StationLocationMap from '../components/StationLocationMap'
import { findStationTemplate } from '../data/stationTemplates'
import { LOCATION_FACILITY_GROUPS, normalizeFacilities } from '../data/locationFacilities'
import './StationEdit.css'

const PROTOCOL_VERSIONS = ['ocpp1.6', 'ocpp2.0.1', 'ocpp2.1']
const CONNECTOR_TYPES = ['Type2', 'CCS', 'CHAdeMO', 'Type1', 'GB/T']
const SUPPORTED_PROFILES = [
  'Core',
  'FirmwareManagement',
  'LocalAuthListManagement',
  'Reservation',
  'SmartCharging',
  'RemoteTrigger'
]
const MEASURANDS = [
  'Energy.Active.Import.Register',
  'Power.Active.Import',
  'Current.Import',
  'Voltage',
  'SoC',
  'Temperature'
]

const TABS = [
  { id: 'general', label: 'General' },
  { id: 'connection', label: 'Connection' },
  { id: 'connectors', label: 'Connectors' },
  { id: 'simulation', label: 'Simulation' },
  { id: 'advanced', label: 'Advanced' }
]

const DEFAULT_FORM_DATA = {
  stationId: '',
  name: '',
  enabled: true,
  autoStart: false,
  protocolVersion: 'ocpp1.6',
  vendor: '',
  model: '',
  serialNumber: '',
  firmwareVersion: '',
  iccid: '',
  imsi: '',
  connectors: [
    { id: 1, type: 'Type2', maxPower: 22000, status: 'Available' }
  ],
  supportedProfiles: ['Core'],
  meterValuesConfig: {
    interval: 60,
    measurands: ['Energy.Active.Import.Register', 'Power.Active.Import'],
    alignedDataInterval: 900
  },
  csmsUrl: '',
  csmsAuth: {
    type: 'none',
    username: '',
    password: ''
  },
  simulation: {
    bootDelay: 0,
    heartbeatInterval: 300,
    statusNotificationOnChange: true,
    defaultIdTag: 'DEFAULT_TAG',
    energyDeliveryRate: 7000,
    randomizeMeterValues: true,
    meterValueVariance: 0.1,
    loadBalancing: true,
    localAuthorize: true
  },
  tags: [],
  location: { latitude: null, longitude: null, name: '', facilities: [] }
}

function emptyLocation() {
  return { latitude: null, longitude: null, name: '', facilities: [] }
}

function locationFromStation(station) {
  const loc = station?.location
  const facilities = normalizeFacilities(loc?.facilities)
  const lat = Number(loc?.latitude ?? station?.latitude)
  const lng = Number(loc?.longitude ?? station?.longitude)
  if (Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180) {
    return {
      latitude: lat,
      longitude: lng,
      name: String(loc?.name || '').trim(),
      facilities
    }
  }
  return {
    ...emptyLocation(),
    name: String(loc?.name || '').trim(),
    facilities
  }
}

function hasCoord(value) {
  return value != null && value !== ''
}

function locationPayload(location) {
  const facilities = normalizeFacilities(location?.facilities)
  const name = String(location?.name || '').trim()
  if (!hasCoord(location?.latitude) || !hasCoord(location?.longitude)) {
    if (facilities.length === 0 && !name) {
      return null
    }
    return {
      latitude: null,
      longitude: null,
      name,
      facilities
    }
  }
  const lat = Number(location.latitude)
  const lng = Number(location.longitude)
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) {
    if (facilities.length === 0 && !name) {
      return null
    }
    return {
      latitude: null,
      longitude: null,
      name,
      facilities
    }
  }
  return {
    latitude: lat,
    longitude: lng,
    name,
    facilities
  }
}

function suggestStationId(template) {
  const base = String(template?.model || template?.name || 'CP')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 20)
  const suffix = Math.random().toString(36).slice(2, 6).toUpperCase()
  return `${base || 'CP'}-${suffix}`
}

function formDataFromTemplate(template) {
  const connectors = Array.isArray(template?.connectors) && template.connectors.length > 0
    ? template.connectors.map((connector, index) => ({
        id: connector.id || index + 1,
        type: connector.type || 'Type2',
        maxPower: connector.maxPower || 22000,
        status: connector.status || 'Available'
      }))
    : DEFAULT_FORM_DATA.connectors.map((connector) => ({ ...connector }))

  return {
    ...DEFAULT_FORM_DATA,
    protocolVersion: template.protocolVersion || DEFAULT_FORM_DATA.protocolVersion,
    vendor: template.vendor || '',
    model: template.model || '',
    serialNumber: template.serialNumber || '',
    firmwareVersion: template.firmwareVersion || '',
    connectors,
    supportedProfiles: template.supportedProfiles?.length
      ? [...template.supportedProfiles]
      : DEFAULT_FORM_DATA.supportedProfiles,
    meterValuesConfig: {
      ...DEFAULT_FORM_DATA.meterValuesConfig,
      ...template.meterValuesConfig
    },
    csmsUrl: template.csmsUrl || '',
    csmsAuth: {
      ...DEFAULT_FORM_DATA.csmsAuth,
      ...template.csmsAuth
    },
    simulation: {
      ...DEFAULT_FORM_DATA.simulation,
      ...template.simulation
    },
    name: template.name || '',
    stationId: suggestStationId(template),
    tags: Array.isArray(template.tags) ? [...template.tags] : [],
    location: emptyLocation(),
    enabled: template.enabled !== false,
    autoStart: Boolean(template.autoStart)
  }
}

function apiErrorMessage(err, fallback) {
  return err.response?.data?.error || err.message || fallback
}

function validateStationForm(formData) {
  if (!String(formData.stationId || '').trim()) {
    return { tab: 'general', message: 'Station ID is required' }
  }
  if (!String(formData.name || '').trim()) {
    return { tab: 'general', message: 'Station name is required' }
  }
  if (!String(formData.vendor || '').trim()) {
    return { tab: 'general', message: 'Vendor is required' }
  }
  if (!String(formData.model || '').trim()) {
    return { tab: 'general', message: 'Model is required' }
  }
  if (!String(formData.csmsUrl || '').trim()) {
    return { tab: 'connection', message: 'CSMS URL is required' }
  }
  if (!formData.connectors?.length) {
    return { tab: 'connectors', message: 'At least one connector is required' }
  }
  for (const connector of formData.connectors) {
    if (!connector.id || connector.id <= 0) {
      return { tab: 'connectors', message: 'Each connector must have a positive ID' }
    }
    if (!connector.type) {
      return { tab: 'connectors', message: 'Each connector must have a type' }
    }
    if (!connector.maxPower || connector.maxPower <= 0) {
      return { tab: 'connectors', message: 'Each connector must have a positive max power' }
    }
  }
  return null
}

function stationPayload(formData) {
  return {
    stationId: String(formData.stationId || '').trim(),
    name: String(formData.name || '').trim(),
    enabled: Boolean(formData.enabled),
    autoStart: Boolean(formData.autoStart),
    protocolVersion: formData.protocolVersion,
    vendor: String(formData.vendor || '').trim(),
    model: String(formData.model || '').trim(),
    serialNumber: formData.serialNumber || '',
    firmwareVersion: formData.firmwareVersion || '',
    iccid: formData.iccid || '',
    imsi: formData.imsi || '',
    connectors: (formData.connectors || []).map((connector) => ({
      id: connector.id,
      type: connector.type,
      maxPower: connector.maxPower
    })),
    supportedProfiles: formData.supportedProfiles || [],
    meterValuesConfig: formData.meterValuesConfig,
    csmsUrl: String(formData.csmsUrl || '').trim(),
    csmsAuth: formData.csmsAuth,
    location: locationPayload(formData.location),
    simulation: formData.simulation,
    tags: formData.tags || []
  }
}

function StationEdit() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const routeLocation = useLocation()
  const { isAdmin } = useAuth()
  const isEditing = Boolean(id)

  const [activeTab, setActiveTab] = useState('general')
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA)
  const [loading, setLoading] = useState(isEditing)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [newTag, setNewTag] = useState('')
  const [appliedTemplateName, setAppliedTemplateName] = useState('')

  useEffect(() => {
    if (isEditing) {
      fetchStation()
      return
    }
    const template = routeLocation.state?.template || findStationTemplate(searchParams.get('template'))
    if (!template) return
    setFormData(formDataFromTemplate(template))
    setAppliedTemplateName(template.name || '')
  }, [id])

  const fetchStation = async () => {
    try {
      setLoading(true)
      const response = await stationsAPI.getById(id)
      const station = response.data
      setFormData({
        ...DEFAULT_FORM_DATA,
        ...station,
        csmsAuth: station.csmsAuth || { type: 'none', username: '', password: '' },
        simulation: { ...DEFAULT_FORM_DATA.simulation, ...station.simulation },
        meterValuesConfig: { ...DEFAULT_FORM_DATA.meterValuesConfig, ...station.meterValuesConfig },
        location: locationFromStation(station)
      })
    } catch (err) {
      setError(`Failed to load station: ${apiErrorMessage(err, err.message)}`)
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleNestedChange = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value }
    }))
  }

  const handleConnectorChange = (index, field, value) => {
    const newConnectors = [...formData.connectors]
    newConnectors[index] = { ...newConnectors[index], [field]: value }
    setFormData(prev => ({ ...prev, connectors: newConnectors }))
  }

  const addConnector = () => {
    const newId = formData.connectors.length > 0
      ? Math.max(...formData.connectors.map(c => c.id)) + 1
      : 1
    setFormData(prev => ({
      ...prev,
      connectors: [...prev.connectors, {
        id: newId,
        type: 'Type2',
        maxPower: 22000,
        status: 'Available'
      }]
    }))
  }

  const removeConnector = (index) => {
    if (formData.connectors.length <= 1) {
      alert('Station must have at least one connector')
      return
    }
    setFormData(prev => ({
      ...prev,
      connectors: prev.connectors.filter((_, i) => i !== index)
    }))
  }

  const handleProfileToggle = (profile) => {
    const profiles = formData.supportedProfiles || []
    if (profiles.includes(profile)) {
      setFormData(prev => ({
        ...prev,
        supportedProfiles: profiles.filter(p => p !== profile)
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        supportedProfiles: [...profiles, profile]
      }))
    }
  }

  const handleMeasurandToggle = (measurand) => {
    const measurands = formData.meterValuesConfig.measurands || []
    if (measurands.includes(measurand)) {
      handleNestedChange('meterValuesConfig', 'measurands',
        measurands.filter(m => m !== measurand)
      )
    } else {
      handleNestedChange('meterValuesConfig', 'measurands',
        [...measurands, measurand]
      )
    }
  }

  const addTag = () => {
    if (newTag && !formData.tags.includes(newTag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...(prev.tags || []), newTag]
      }))
      setNewTag('')
    }
  }

  const removeTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }))
  }

  const handleFacilityToggle = (facilityId) => {
    setFormData(prev => {
      const current = normalizeFacilities(prev.location?.facilities)
      const facilities = current.includes(facilityId)
        ? current.filter((id) => id !== facilityId)
        : [...current, facilityId]
      return {
        ...prev,
        location: {
          ...prev.location,
          facilities
        }
      }
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const validationError = validateStationForm(formData)
    if (validationError) {
      setActiveTab(validationError.tab)
      setError(validationError.message)
      return
    }
    try {
      setSaving(true)
      setError(null)
      const payload = stationPayload(formData)
      if (isEditing) {
        await stationsAPI.update(id, payload)
      } else {
        await stationsAPI.create(payload)
      }
      navigate('/stations')
    } catch (err) {
      setError(`Failed to ${isEditing ? 'update' : 'create'} station: ${apiErrorMessage(err, 'Unknown error')}`)
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!confirm(`Are you sure you want to delete station "${formData.name}"?`)) {
      return
    }
    try {
      setSaving(true)
      await stationsAPI.delete(id)
      navigate('/stations')
    } catch (err) {
      setError(`Failed to delete station: ${apiErrorMessage(err, err.message)}`)
      setSaving(false)
    }
  }

  if (!isAdmin) {
    return (
      <div className="station-edit">
        <div className="error-message">You do not have permission to access this page.</div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="station-edit">
        <div className="loading">Loading station...</div>
      </div>
    )
  }

  return (
    <div className="station-edit">
      <div className="page-header">
        <nav className="breadcrumb">
          <Link to="/stations">Stations</Link>
          <span className="breadcrumb-separator">/</span>
          <span>{isEditing ? 'Edit' : 'New Station'}</span>
        </nav>
        <div className="page-header__row">
          <h2>{isEditing ? `Edit: ${formData.name || formData.stationId}` : 'Create New Station'}</h2>
        </div>
      </div>

      {appliedTemplateName && !isEditing && (
        <div className="info-message">Using template: {appliedTemplateName}. Set a unique Station ID and CSMS URL before creating.</div>
      )}
      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit} className="station-edit__form">
        {/* Tabs */}
        <div className="edit-tabs">
          {TABS.map(tab => (
            <button
              key={tab.id}
              type="button"
              className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
              {tab.id === 'connectors' && (
                <span className="tab-badge">{formData.connectors.length}</span>
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="edit-body">
          {/* General Tab */}
          {activeTab === 'general' && (
            <div className="edit-tab">
              <section className="form-section">
                <h3>Basic Information</h3>
                <div className="form-grid">
                  <div className="form-field">
                    <label>Station ID *</label>
                    <input
                      type="text"
                      value={formData.stationId}
                      onChange={(e) => handleChange('stationId', e.target.value)}
                      required
                      disabled={isEditing}
                      placeholder="e.g., CP001"
                    />
                    {isEditing && <small>Cannot be changed after creation</small>}
                  </div>

                  <div className="form-field">
                    <label>Station Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      required
                      placeholder="e.g., Main Street Station"
                    />
                  </div>

                  <div className="form-field">
                    <label>Protocol Version *</label>
                    <select
                      value={formData.protocolVersion}
                      onChange={(e) => handleChange('protocolVersion', e.target.value)}
                      required
                    >
                      {PROTOCOL_VERSIONS.map(v => (
                        <option key={v} value={v}>{v.toUpperCase()}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-field form-field--checkboxes">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.enabled}
                        onChange={(e) => handleChange('enabled', e.target.checked)}
                      />
                      Enabled
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.autoStart}
                        onChange={(e) => handleChange('autoStart', e.target.checked)}
                      />
                      Auto Start
                    </label>
                  </div>
                </div>
              </section>

              <section className="form-section">
                <h3>Device Information</h3>
                <div className="form-grid">
                  <div className="form-field">
                    <label>Vendor *</label>
                    <input
                      type="text"
                      value={formData.vendor}
                      onChange={(e) => handleChange('vendor', e.target.value)}
                      required
                      placeholder="e.g., ABB"
                    />
                  </div>

                  <div className="form-field">
                    <label>Model *</label>
                    <input
                      type="text"
                      value={formData.model}
                      onChange={(e) => handleChange('model', e.target.value)}
                      required
                      placeholder="e.g., Terra AC"
                    />
                  </div>

                  <div className="form-field">
                    <label>Serial Number</label>
                    <input
                      type="text"
                      value={formData.serialNumber}
                      onChange={(e) => handleChange('serialNumber', e.target.value)}
                      placeholder="e.g., SN123456789"
                    />
                  </div>

                  <div className="form-field">
                    <label>Firmware Version</label>
                    <input
                      type="text"
                      value={formData.firmwareVersion}
                      onChange={(e) => handleChange('firmwareVersion', e.target.value)}
                      placeholder="e.g., 1.2.3"
                    />
                  </div>
                </div>
              </section>

              <section className="form-section">
                <h3>Location map</h3>
                <StationLocationMap
                  value={formData.location}
                  onChange={(location) => handleChange('location', {
                    ...formData.location,
                    ...location,
                    facilities: formData.location?.facilities || []
                  })}
                />
                <div className="form-field form-field--wide facility-field">
                  <label>Facilities available</label>
                  <small>Optional. Select all amenities at this CPO location.</small>
                  <div className="facility-groups">
                    {LOCATION_FACILITY_GROUPS.map((group) => (
                      <div key={group.id} className="facility-group">
                        <h4>{group.label}</h4>
                        <div className="checkbox-grid">
                          {group.options.map((option) => (
                            <label key={option.id} className="checkbox-label">
                              <input
                                type="checkbox"
                                checked={formData.location?.facilities?.includes(option.id)}
                                onChange={() => handleFacilityToggle(option.id)}
                              />
                              {option.label}
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            </div>
          )}

          {/* Connection Tab */}
          {activeTab === 'connection' && (
            <div className="edit-tab">
              <section className="form-section">
                <h3>CSMS Connection</h3>
                <div className="form-grid">
                  <div className="form-field form-field--wide">
                    <label>CSMS URL *</label>
                    <input
                      type="text"
                      value={formData.csmsUrl}
                      onChange={(e) => handleChange('csmsUrl', e.target.value)}
                      required
                      placeholder="ws://localhost:9000/ocpp"
                    />
                    <small>WebSocket URL of the Central System</small>
                  </div>

                  <div className="form-field">
                    <label>Authentication Type</label>
                    <select
                      value={formData.csmsAuth?.type || 'none'}
                      onChange={(e) => handleNestedChange('csmsAuth', 'type', e.target.value)}
                    >
                      <option value="none">None</option>
                      <option value="basic">Basic Auth</option>
                    </select>
                  </div>

                  {formData.csmsAuth?.type === 'basic' && (
                    <>
                      <div className="form-field">
                        <label>Username</label>
                        <input
                          type="text"
                          value={formData.csmsAuth?.username || ''}
                          onChange={(e) => handleNestedChange('csmsAuth', 'username', e.target.value)}
                        />
                      </div>

                      <div className="form-field">
                        <label>Password</label>
                        <input
                          type="password"
                          value={formData.csmsAuth?.password || ''}
                          onChange={(e) => handleNestedChange('csmsAuth', 'password', e.target.value)}
                        />
                      </div>
                    </>
                  )}
                </div>
              </section>
            </div>
          )}

          {/* Connectors Tab */}
          {activeTab === 'connectors' && (
            <div className="edit-tab">
              <section className="form-section">
                <div className="section-header">
                  <h3>Connectors</h3>
                  <button type="button" className="btn btn--sm btn--primary" onClick={addConnector}>
                    + Add Connector
                  </button>
                </div>

                <div className="connectors-grid">
                  {formData.connectors.map((connector, index) => (
                    <div key={index} className="connector-item">
                      <div className="connector-item__header">
                        <span className="connector-item__title">Connector {connector.id}</span>
                        {formData.connectors.length > 1 && (
                          <button
                            type="button"
                            className="btn btn--xs btn--ghost btn--danger"
                            onClick={() => removeConnector(index)}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      <div className="connector-item__fields">
                        <div className="form-field">
                          <label>Type</label>
                          <select
                            value={connector.type}
                            onChange={(e) => handleConnectorChange(index, 'type', e.target.value)}
                          >
                            {CONNECTOR_TYPES.map(t => (
                              <option key={t} value={t}>{t}</option>
                            ))}
                          </select>
                        </div>
                        <div className="form-field">
                          <label>Max Power (W)</label>
                          <input
                            type="number"
                            value={connector.maxPower}
                            onChange={(e) => handleConnectorChange(index, 'maxPower', parseInt(e.target.value))}
                            min="0"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </div>
          )}

          {/* Simulation Tab */}
          {activeTab === 'simulation' && (
            <div className="edit-tab">
              <section className="form-section">
                <h3>Timing</h3>
                <div className="form-grid">
                  <div className="form-field">
                    <label>Boot Delay (sec)</label>
                    <input
                      type="number"
                      value={formData.simulation.bootDelay}
                      onChange={(e) => handleNestedChange('simulation', 'bootDelay', parseInt(e.target.value))}
                      min="0"
                    />
                    <small>Delay before sending BootNotification</small>
                  </div>

                  <div className="form-field">
                    <label>Heartbeat Interval (sec)</label>
                    <input
                      type="number"
                      value={formData.simulation.heartbeatInterval}
                      onChange={(e) => handleNestedChange('simulation', 'heartbeatInterval', parseInt(e.target.value))}
                      min="1"
                    />
                  </div>
                </div>
              </section>

              <section className="form-section">
                <h3>Charging Simulation</h3>
                <div className="form-grid">
                  <div className="form-field">
                    <label>Default ID Tag</label>
                    <input
                      type="text"
                      value={formData.simulation.defaultIdTag}
                      onChange={(e) => handleNestedChange('simulation', 'defaultIdTag', e.target.value)}
                    />
                  </div>

                  <div className="form-field">
                    <label>Energy Rate (W)</label>
                    <input
                      type="number"
                      value={formData.simulation.energyDeliveryRate}
                      onChange={(e) => handleNestedChange('simulation', 'energyDeliveryRate', parseInt(e.target.value))}
                      min="0"
                    />
                  </div>

                  <div className="form-field">
                    <label>Meter Variance (0-1)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.simulation.meterValueVariance}
                      onChange={(e) => handleNestedChange('simulation', 'meterValueVariance', parseFloat(e.target.value))}
                      min="0"
                      max="1"
                    />
                  </div>

                  <div className="form-field form-field--checkboxes">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.simulation.randomizeMeterValues}
                        onChange={(e) => handleNestedChange('simulation', 'randomizeMeterValues', e.target.checked)}
                      />
                      Randomize Meters
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.simulation.statusNotificationOnChange ?? true}
                        onChange={(e) => handleNestedChange('simulation', 'statusNotificationOnChange', e.target.checked)}
                      />
                      Status on Change
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.simulation.loadBalancing !== false}
                        onChange={(e) => handleNestedChange('simulation', 'loadBalancing', e.target.checked)}
                      />
                      Dynamic Load Balancing
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.simulation.localAuthorize !== false}
                        onChange={(e) => handleNestedChange('simulation', 'localAuthorize', e.target.checked)}
                      />
                      Local authorize if CSMS rejects RFID
                    </label>
                  </div>
                </div>
              </section>
            </div>
          )}

          {/* Advanced Tab */}
          {activeTab === 'advanced' && (
            <div className="edit-tab">
              <section className="form-section">
                <h3>Meter Values Configuration</h3>
                <div className="form-grid">
                  <div className="form-field">
                    <label>Interval (sec)</label>
                    <input
                      type="number"
                      value={formData.meterValuesConfig.interval}
                      onChange={(e) => handleNestedChange('meterValuesConfig', 'interval', parseInt(e.target.value))}
                      min="1"
                    />
                  </div>

                  <div className="form-field">
                    <label>Aligned Interval (sec)</label>
                    <input
                      type="number"
                      value={formData.meterValuesConfig.alignedDataInterval}
                      onChange={(e) => handleNestedChange('meterValuesConfig', 'alignedDataInterval', parseInt(e.target.value))}
                      min="1"
                    />
                  </div>
                </div>

                <div className="form-field form-field--wide">
                  <label>Measurands</label>
                  <div className="checkbox-grid">
                    {MEASURANDS.map(measurand => (
                      <label key={measurand} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={formData.meterValuesConfig.measurands?.includes(measurand)}
                          onChange={() => handleMeasurandToggle(measurand)}
                        />
                        {measurand}
                      </label>
                    ))}
                  </div>
                </div>
              </section>

              <section className="form-section">
                <h3>Hardware Identifiers</h3>
                <div className="form-grid">
                  <div className="form-field">
                    <label>ICCID (SIM)</label>
                    <input
                      type="text"
                      value={formData.iccid || ''}
                      onChange={(e) => handleChange('iccid', e.target.value)}
                    />
                  </div>

                  <div className="form-field">
                    <label>IMSI</label>
                    <input
                      type="text"
                      value={formData.imsi || ''}
                      onChange={(e) => handleChange('imsi', e.target.value)}
                    />
                  </div>
                </div>
              </section>

              <section className="form-section">
                <h3>Supported Profiles</h3>
                <div className="checkbox-grid">
                  {SUPPORTED_PROFILES.map(profile => (
                    <label key={profile} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.supportedProfiles?.includes(profile)}
                        onChange={() => handleProfileToggle(profile)}
                      />
                      {profile}
                    </label>
                  ))}
                </div>
              </section>

              <section className="form-section">
                <h3>Tags</h3>
                <div className="tags-input">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    placeholder="Add tag..."
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  />
                  <button type="button" className="btn btn--sm btn--secondary" onClick={addTag}>Add</button>
                </div>
                {formData.tags?.length > 0 && (
                  <div className="tags-list">
                    {formData.tags.map(tag => (
                      <span key={tag} className="tag">
                        {tag}
                        <button type="button" onClick={() => removeTag(tag)}>x</button>
                      </span>
                    ))}
                  </div>
                )}
              </section>
            </div>
          )}
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <div className="form-actions__left">
            {isEditing && (
              <button
                type="button"
                className="btn btn--danger"
                onClick={handleDelete}
                disabled={saving}
              >
                Delete Station
              </button>
            )}
          </div>
          <div className="form-actions__right">
            <Link to="/stations" className="btn btn--secondary">
              Cancel
            </Link>
            <button type="submit" className="btn btn--primary" disabled={saving}>
              {saving ? 'Saving...' : (isEditing ? 'Save Changes' : 'Create Station')}
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}

export default StationEdit
